import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Box,
    Table,
    Button,
    TextFilter,
    Pagination,
    CollectionPreferences,
} from '@amzn/awsui-components-console';
import React, { useContext, useEffect, useState } from 'react';
import { fetchDevices, getUserPrivateGroups } from '../apis';
import { getCurrentAuthUserEmail, isAdmin } from '../../../lib/utils';
import * as Constants from '../constants';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState, getFilterCounterText } from '../utils.jsx';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { RegionContext } from '../../../CustomContext';
import { useNotifications } from '../../../NotificationContext';
import MoveDeviceModal from '../MoveDeviceModal';

export default function GroupDevicesManager() {
    const history = useHistory();
    const location = useLocation();
    const { groupId: urlGroupId } = useParams();
    const [loading, setLoading] = useState(false);
    const [devices, setDevices] = useState([]);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [isDdfAdmin, setIsDdfAdmin] = useState(false);
    const { selectedRegion } = useContext(RegionContext);
    const { addError } = useNotifications();
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
        visibleContent: ['deviceName', 'serial', 'status', 'owner', 'groupName'],
    });

    const groupId = urlGroupId || null;
    const [groupName, setGroupName] = useState(location.state?.groupName || null);

    useEffect(() => {
        setGroupName(location.state?.groupName || null);
    }, [location.state?.groupName]);

    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(
        devices,
        {
            filtering: {
                noMatch: (
                    <EmptyState
                        title="No matches"
                        subtitle="We can't find a match."
                        action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                    />
                ),
            },
            pagination: { pageSize: preferences.pageSize },
            sorting: {
                defaultState: { sortingColumn: Constants.DEVICE_SORTED_FIELD.sortingColumn, isDescending: true },
            },
            selection: {},
        }
    );

    const loadDevices = async () => {
        setLoading(true);
        try {
            const [devicesResponse, groupsResponse, adminResponse] = await Promise.all([
                fetchDevices(selectedRegion.value, groupId),
                getUserPrivateGroups(selectedRegion.value),
                isAdmin(),
            ]);

            const devicesData = devicesResponse.response || [];
            const privateGroups = groupsResponse.response || [];
            const currentUserEmail = await getCurrentAuthUserEmail();
            const isUserDdfAdmin = adminResponse.status || false;

            setIsDdfAdmin(isUserDdfAdmin);

            // Resolve group name from API
            if (groupId) {
                const matchedGroup = privateGroups.find((g) => g.id === groupId);
                if (matchedGroup) {
                    setGroupName(matchedGroup.name);
                } else if (!groupName) {
                    setGroupName(groupId);
                }
            }

            const userPrivateGroups = privateGroups.filter((group) => group.owner?.email === currentUserEmail);

            const devicesWithMoveInfo = devicesData.map((device) => {
                const deviceOwner = device.group?.owner?.email || device.owner?.email;
                const deviceInUserGroup = userPrivateGroups.some((group) => group.id === device.group?.id);
                const canMove = isUserDdfAdmin || (deviceInUserGroup && userPrivateGroups.length > 1);

                return {
                    ...device,
                    canMove,
                    deviceInUserGroup,
                };
            });

            setDevices(devicesWithMoveInfo);
        } catch (error) {
            console.error('Failed to load devices:', error);
            addError('Failed to load devices. Please try again.', 'Error Loading Devices');
            setDevices([]);
        }
        setLoading(false);
    };

    const handleMoveDevices = () => {
        if (selectedDevices.length > 0) {
            setShowMoveModal(true);
        }
    };

    const canMoveSelectedDevices = () => {
        return selectedDevices.length > 0 && selectedDevices.every((device) => device.canMove);
    };

    const onSelectionChange = ({ detail }) => {
        setSelectedDevices(detail.selectedItems);
    };

    const handleMoveSuccess = async () => {
        setSelectedDevices([]);
        await loadDevices();
    };

    useEffect(() => {
        if (!groupId) {
            history.replace('/lab-management/groups');
            return;
        }
        loadDevices();
    }, [selectedRegion, groupId]);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Group management', href: '#/lab-management/groups' },
                    { text: 'Devices', href: `#/lab-management/groups/${groupId}/devices` },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description={`Manage devices in the ${groupName || 'selected'} group. You can move devices between your private groups if you own multiple private groups.`}
            >
                Device Management - {groupName || 'selected'}
            </Header>

            <Table
                {...collectionProps}
                onSelectionChange={onSelectionChange}
                selectedItems={selectedDevices}
                selectionType="multi"
                header={
                    <Header
                        counter={`(${devices.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={loadDevices} iconName="refresh" variant="icon" />
                                <Button
                                    onClick={handleMoveDevices}
                                    variant="primary"
                                    disabled={selectedDevices.length === 0 || !canMoveSelectedDevices()}
                                >
                                    Move Selected ({selectedDevices.length})
                                </Button>
                            </SpaceBetween>
                        }
                        description={`Devices in ${groupName || 'selected'} group`}
                    >
                        Devices
                    </Header>
                }
                columnDefinitions={Constants.DEVICE_COLUMNS_DEFINITION}
                visibleColumns={preferences.visibleContent}
                items={items}
                pagination={<Pagination {...paginationProps} />}
                filter={<TextFilter {...filterProps} countText={getFilterCounterText(filteredItemsCount)} />}
                empty={
                    <Box textAlign="center" color="inherit">
                        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                            No devices to display
                        </Box>
                    </Box>
                }
                loading={loading}
                loadingText="Loading devices"
                stickyColumns={preferences.stickyColumns}
                resizableColumns
                preferences={
                    <CollectionPreferences
                        {...Constants.DEVICE_COLLECTION_PREFERENCES_PROPS}
                        preferences={preferences}
                        onConfirm={({ detail }) => setPreferences(detail)}
                    />
                }
            />

            <MoveDeviceModal
                visible={showMoveModal}
                onDismiss={() => setShowMoveModal(false)}
                devices={selectedDevices}
                onMoveSuccess={handleMoveSuccess}
            />
        </SpaceBetween>
    );
}
